import { useState, useEffect } from 'react';
import { doc, onSnapshot, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../config/firebase';

const DEFAULT_INFO_BANNER = {
  visible: true,
  title: 'Data Collection Note:',
  message: 'Fetching data from Bazaraki URLs (both individual items and searches) may be blocked by browser security (CORS). If so, input data manually. A backend proxy is needed for reliable automated fetching. Always respect website Terms of Service.',
  type: 'warning'
};

export const useAppSettings = () => {
  const [infoBanner, setInfoBanner] = useState(DEFAULT_INFO_BANNER);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!db) {
      setIsLoading(false);
      return;
    }

    const infoBannerRef = doc(db, 'artifacts/bz-tracker/appSettings/infoBanner');

    const unsubscribe = onSnapshot(
      infoBannerRef,
      (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          setInfoBanner({
            visible: data.visible ?? DEFAULT_INFO_BANNER.visible,
            title: data.title ?? DEFAULT_INFO_BANNER.title,
            message: data.message ?? DEFAULT_INFO_BANNER.message,
            type: data.type ?? DEFAULT_INFO_BANNER.type
          });
        } else {
          // Use defaults if no settings exist
          setInfoBanner(DEFAULT_INFO_BANNER);
        }
        setIsLoading(false);
        setError(null);
      },
      (err) => {
        console.error('Error fetching app settings:', err);
        setError(err.message);
        setIsLoading(false);
        // Keep using defaults on error
        setInfoBanner(DEFAULT_INFO_BANNER);
      }
    );

    return () => unsubscribe();
  }, []);

  const updateInfoBanner = async (newSettings, userId) => {
    if (!db) {
      throw new Error('Database not available');
    }

    const infoBannerRef = doc(db, 'artifacts/bz-tracker/appSettings/infoBanner');

    await setDoc(infoBannerRef, {
      visible: newSettings.visible,
      title: newSettings.title,
      message: newSettings.message,
      type: newSettings.type,
      updatedAt: serverTimestamp(),
      updatedBy: userId
    });
  };

  return {
    infoBanner,
    isLoading,
    error,
    updateInfoBanner
  };
};
